import type { DataTrust, ProcessingStatus, VerificationStatus } from '@clm/shared-types';

/**
 * The trust and pipeline labels, in one place — CLM-108 (R-108-14, R-108-15,
 * D-2.1-4, CR-06). A plain module (no component), the `pipeline-status-chips.ts`
 * pattern, so fast refresh stays whole and every surface imports the same words.
 *
 * ## Why "Verified" lives here and nowhere else
 *
 * Before CLM-108, `processing_status = 'Verified'` meant a person had settled
 * every field, so surfaces printed it — raw, or through four copies of a label
 * map — as "Verified". It no longer means that: an auto-approved contract also
 * reaches the end of the pipeline axis (B-02 keeps the axes apart, and a ninth
 * processing status was not decided). Printing it as "Verified" would say a
 * person read every field of a contract nobody fully read — the failure D-2.1-4
 * exists to prevent. So:
 *
 *  - **`processingLabel`** is the pipeline axis. Its `Verified` reads
 *    **"Settled"**: nothing is outstanding, and nothing more is claimed.
 *  - **`TRUST_LABELS`** is the trust axis (`data_trust`), and it is the **only**
 *    source of a contract-level "Verified" — and only for `data_trust =
 *    'Verified'`. Not `processing_status`, and not "nothing outstanding"
 *    (`has_unverified_fields = false`), which is also true of a contract with
 *    no field row at all.
 *  - **`VERIFICATION_STATUS_COPY`** is the field axis: the badge copy for one
 *    `field_verification` row. Its `Verified` and `Corrected` are human; its
 *    `AutoApproved` is "Auto-approved (policy)" in the `ai` tone — never `ok`,
 *    so a policy settlement never looks like a person's.
 *
 * Each is a `Record` over its vocabulary, so a sixth trust value or a ninth
 * processing status fails to compile here instead of rendering a raw string.
 */

/** The trust axis — `clm.contract.data_trust` — as a person reads it (R-108-14). */
export const TRUST_LABELS: Readonly<Record<DataTrust, string>> = {
  Verified: 'Verified by people',
  AutoApproved: 'Settled — includes auto-approved fields',
  PendingVerification: 'Awaiting verification',
  PendingExtraction: 'Not yet extracted',
  ExtractionFailed: 'Extraction failed',
};

/**
 * The tag tone of each trust value — existing token pairs only. `ok` only for
 * every field settled by a person; `ai` for a policy settlement (the badge's
 * tone, CR-06), so the two never share a colour.
 */
export const TRUST_TONES: Readonly<Record<DataTrust, string>> = {
  Verified: 'ok',
  AutoApproved: 'ai',
  PendingVerification: 'acc',
  PendingExtraction: 'out',
  ExtractionFailed: 'bad',
};

/**
 * The pipeline axis as a person reads it. The values the Analytics boards and
 * the Pipeline page already used (`Pending` for `PendingExtraction`, …), with
 * `Verified` → **"Settled"** (R-108-14). The three label maps that existed
 * before CLM-108 now read from this one.
 */
export const PROCESSING_LABELS: Readonly<Record<ProcessingStatus, string>> = {
  PendingExtraction: 'Pending',
  Converted: 'Converted',
  Pseudonymised: 'Pseudonymised',
  Extracted: 'Extracted',
  PendingVerification: 'Pending verification',
  Verified: 'Settled',
  ExtractionFailed: 'Extraction failed',
  manual_entry_pending: 'Manual entry (§4C)',
};

/** One pipeline state as a person reads it. Every surface prints `processing_status` through this, never raw. */
export function processingLabel(status: ProcessingStatus): string {
  return PROCESSING_LABELS[status];
}

/**
 * One field row's status, as the badge and the queue's status column show it.
 * `Verified` and `Corrected` stay apart (NFR-12's correction rate); `AutoApproved`
 * names the policy and carries the `ai` tone (CR-06).
 */
export const VERIFICATION_STATUS_COPY: Readonly<
  Record<VerificationStatus, { readonly label: string; readonly tone: string }>
> = {
  Pending: { label: 'Awaiting verification', tone: 'out' },
  Rejected: { label: 'Rejected', tone: 'bad' },
  Verified: { label: 'Verified', tone: 'ok' },
  Corrected: { label: 'Corrected', tone: 'acc' },
  AutoApproved: { label: 'Auto-approved (policy)', tone: 'ai' },
};

/** A field row's status label — the badge's words, for a sentence such as "Recorded as …". */
export function verificationStatusLabel(status: VerificationStatus): string {
  return VERIFICATION_STATUS_COPY[status].label;
}
